import * as THREE from 'three';
import { sceneHelper } from './helper/scene-helper';
import type { AnimationAction, AnimationClip, Object3D } from 'three';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { debugLog } from './debug-log';


export function onAnimationsUpdatedOnce(callback: () => void) {
  window.addEventListener('animationsUpdated', callback, { once: true });
}

export class AnimationManager {
  animationsUpdated = new Event('animationsUpdated');
  animationMixers = new Map<string, THREE.AnimationMixer>();
  /** Primary (exclusive) action per object — replaced when a non-parallel clip starts. */
  currentAnimationActions = new Map<string, AnimationAction>();
  /** Layered actions that keep running across primary clip changes. */
  parallelAnimationActions = new Map<string, Set<AnimationAction>>();
  animatedObjects: Object3D[] = [];
  animationClips = new Map<string, AnimationClip[]>();
  timer = new THREE.Timer();
  private fpsLogElapsedSeconds = 0;
  private fpsLogFrameCount = 0;
  private onUpdateSceneCalled = () => this.updateScene();

  constructor() {
    this.timer.connect(document);
    window.addEventListener('updateSceneCalled', this.onUpdateSceneCalled);
  }

  dispose() {
    window.removeEventListener('updateSceneCalled', this.onUpdateSceneCalled);
    this.timer.disconnect();
    this.stopAllAnimations();
    this.animationMixers.clear();
    this.currentAnimationActions.clear();
    this.parallelAnimationActions.clear();
    this.animationClips.clear();
    this.animatedObjects.length = 0;
  }

  updateScene() {
    this.timer.update();
    const delta = this.timer.getDelta();
    for (const mixer of this.animationMixers.values()) {
      mixer.update(delta);
    }
    window.dispatchEvent(this.animationsUpdated);

    /*this.fpsLogElapsedSeconds += delta;
    this.fpsLogFrameCount += 1;
    if (this.fpsLogElapsedSeconds >= 1) {
      const avgFps = this.fpsLogFrameCount / this.fpsLogElapsedSeconds;
      debugLog(`avg fps (last ~1s): ${avgFps.toFixed(1)}`);
      console.log(`avg fps (last ~1s): ${avgFps.toFixed(1)}`);
      this.fpsLogElapsedSeconds = 0;
      this.fpsLogFrameCount = 0;
    }*/
  }

  disableFrustumCulling(object: Object3D)
  {
    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.frustumCulled = false;
      }
    });
  }

  addAnimationMixerFromGLTF(objectId: string, gltf: GLTF) {
    // disable frustum culling on animated objects, since this causes problems, when objects move out of their bounding box because of animation
    this.disableFrustumCulling(gltf.scene);

    const mixer = new THREE.AnimationMixer(gltf.scene);
    this.animationMixers.set(objectId, mixer);
    this.animationClips.set(objectId, gltf.animations);
  }

  async playAnimation(
    objectId: string,
    animationClipName: string,
    loop: boolean,
    speed: number,
    transitionDuration = 0.2,
    parallel = false,
    weight = 1
  ) {
    const targetWeight = Number.isFinite(weight) ? Math.max(0, weight) : 1;
    const clips = this.animationClips.get(objectId);
    const animationClip = clips?.find((clip) => clip.name === animationClipName);

    if (!animationClip) {
      console.error(`Animation clip ${animationClipName} not found for object ${objectId}`);
      return;
    }

    const primaryAction = this.currentAnimationActions.get(objectId);
    const parallelActions = this.getParallelActions(objectId);
    const mixer = this.animationMixers.get(objectId)!;
    let action = mixer.clipAction(animationClip);

    // Never reuse an already-tracked action: resetting it would interrupt that clip.
    if (action === primaryAction || parallelActions.has(action)) {
      action = this.workaroundForCrossFadingIdenticalClips(objectId, animationClip, action);
    }

    // Parallel layers should release on finish; clamping would leave a stuck pose
    // that Stop can no longer find (action is untracked after "finished").
    action.clampWhenFinished = !parallel;
    action.reset();
    action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    action.timeScale = speed;
    // fadeIn interpolates 0→1 against action.weight, so this is the target blend weight.
    action.weight = targetWeight;

    if (!parallel && primaryAction && primaryAction !== action) {
      primaryAction.fadeOut(transitionDuration);
    }

    if (transitionDuration > 0) {
      action.fadeIn(transitionDuration);
    } else {
      action.setEffectiveWeight(targetWeight);
    }
    action.play();

    if (parallel) {
      parallelActions.add(action);
    } else {
      this.currentAnimationActions.set(objectId, action);
    }

    // Looping clips do not emit the mixer's "finished" event.
    if (loop) return;

    await new Promise<void>((resolve) => {
      const onFinished = (event: { action: AnimationAction }) => {
        if (event.action !== action) return;
        mixer.removeEventListener('finished', onFinished as () => void);
        parallelActions.delete(action);
        resolve();
      };
      mixer.addEventListener('finished', onFinished as () => void);
    });
  }

  stopAnimation(objectId: string, clipName?: string, transitionDuration = 0) {
    if (!objectId) return;
    const mixer = this.animationMixers.get(objectId);
    if (!mixer) return;

    const fadeSeconds = Number.isFinite(transitionDuration)
      ? Math.max(0, transitionDuration)
      : 0;
    const normalizedClip = typeof clipName === 'string' ? clipName.trim() : '';
    const shouldStopAction = (action: AnimationAction) =>
      !normalizedClip || action.getClip().name === normalizedClip;

    const endAction = (action: AnimationAction) => {
      mixer.dispatchEvent({ type: 'finished', action, direction: 1 });
      if (fadeSeconds > 0) {
        action.fadeOut(fadeSeconds);
      } else {
        action.stop();
      }
    };

    const primaryAction = this.currentAnimationActions.get(objectId);
    if (primaryAction && shouldStopAction(primaryAction)) {
      endAction(primaryAction);
      this.currentAnimationActions.delete(objectId);
    }

    const parallelActions = this.parallelAnimationActions.get(objectId);
    if (parallelActions) {
      for (const action of [...parallelActions]) {
        if (!shouldStopAction(action)) continue;
        endAction(action);
        parallelActions.delete(action);
      }
    }
  }

  stopAllAnimations() {
    for (const objectId of this.animationMixers.keys()) {
      this.stopAnimation(objectId);
    }
  }

  private getParallelActions(objectId: string): Set<AnimationAction> {
    let actions = this.parallelAnimationActions.get(objectId);
    if (!actions) {
      actions = new Set();
      this.parallelAnimationActions.set(objectId, actions);
    }
    return actions;
  }

  workaroundForCrossFadingIdenticalClips(
    objectId: string,
    animationClip: AnimationClip,
    _action: AnimationAction
  ) {
    const mixer = this.animationMixers.get(objectId)!;
    const objectAnimationClips = this.animationClips.get(objectId)!;
    const primaryAction = this.currentAnimationActions.get(objectId);
    const parallelActions = this.parallelAnimationActions.get(objectId);
    const sameAnimationClips = objectAnimationClips.filter((c) => c.name === animationClip.name);

    for (const clip of sameAnimationClips) {
      const candidate = mixer.existingAction(clip) ?? mixer.clipAction(clip);
      // Prefer a clipAction that is not currently tracked as primary or parallel.
      if (candidate !== primaryAction && !parallelActions?.has(candidate)) {
        // If it was never created before, clipAction above created it — still unused.
        if (!candidate.isRunning()) return candidate;
        // Running but untracked (e.g. mid fade-out): keep looking / clone.
      }
    }

    const animationClipClone = animationClip.clone();
    objectAnimationClips.push(animationClipClone);
    return mixer.clipAction(animationClipClone);
  }

}
